/**
 * Les flows de la démo — le geste n°1 du chatteur sur un nouveau fan.
 *
 * Au poste, « Flow ▾ › script 1 » lance un flow de la créatrice : c'est LUI
 * qui écrit les premières minutes (la chauffe), et il se termine sur « mais
 * pour continuer, tu vas devoir me payer ». Le chatteur reprend là.
 *
 * Ici on joue le VRAI flow : sa définition est lue en prod
 * (`GET /api/creators/{id}/flows/{flowId}`, une lecture de configuration qui
 * ne porte aucun fan), et ses bulles — textes et médias du Vault — sont
 * posées dans le fil de la démo, au rythme d'un flow raccourci pour la caméra.
 * Quand une étape attend la réponse du fan, le fan répond (`pendantLeFlow`).
 *
 * ⛔ Le DÉPART du flow, lui, ne va jamais en prod : `POST …/start` porte un
 * `conversation_id`, donc un fan. Il est servi ici, comme toute écriture.
 */
import type { FlowDetail, FlowNodeBackend, FlowRun, FlowSummary } from "@/panels/flow/api";

import { emettre } from "./bus";
import { traduireVers } from "./langues";
import { plusTard } from "./minuteries";
import {
  conversation,
  idDemo,
  message,
  poserLeMessage,
  toutesLesConversations,
  type ConversationDemo,
} from "./monde";
import { dire, marquerLeFlow, repliqueDuFan } from "./scenario";

/** Entre deux bulles du flow (2 s en prod). */
const ENTRE_DEUX_BULLES_MS = 1_800;
/** Une pause du flow (90 s en prod avant une vidéo) — raccourcie. */
const PAUSE_MS = 4_000;
/** Le temps que le fan met à répondre au flow. */
const REPONSE_DU_FAN_MS = 4_500;

/* ────────────────────────────────────────────────────────────────────────── */
/* Lire la prod — la configuration des flows, jamais un fan                   */
/* ────────────────────────────────────────────────────────────────────────── */

const listes = new Map<string, Promise<FlowSummary[]>>();
const details = new Map<string, Promise<FlowDetail | null>>();

async function client() {
  // 🪤 Import DYNAMIQUE : `core/api/client` importe la démo ; l'inverse,
  //    statique, fermerait le cycle au chargement.
  return (await import("@/core/api/client")).api;
}

/** Les flows de la créatrice, tels que la prod les sert (sans fan). */
export function lesFlowsDe(creatorId: string): Promise<FlowSummary[]> {
  let p = listes.get(creatorId);
  if (!p) {
    p = client()
      .then((api) => api.get<FlowSummary[]>(`/api/creators/${encodeURIComponent(creatorId)}/flows`))
      .catch(() => []);
    listes.set(creatorId, p);
  }
  return p;
}

function leFlow(creatorId: string, flowId: string): Promise<FlowDetail | null> {
  const cle = `${creatorId}/${flowId}`;
  let p = details.get(cle);
  if (!p) {
    p = client()
      .then((api) =>
        api.get<FlowDetail>(
          `/api/creators/${encodeURIComponent(creatorId)}/flows/${encodeURIComponent(flowId)}`,
        ),
      )
      .catch(() => null);
    details.set(cle, p);
  }
  return p;
}

/** Le premier flow ACTIF que ce fan n'a pas encore reçu — celui que le poste
 *  suggère sur un nouveau fan. `null` s'il les a tous eus. */
export async function leFlowASuggerer(conv: ConversationDemo): Promise<FlowSummary | null> {
  const flows = await lesFlowsDe(conv.fiche.creator_id);
  return (
    [...flows]
      .filter((f) => f.is_active)
      .sort((a, b) => a.position - b.position)
      .find((f) => !conv.flowsRecus.includes(f.name)) ?? null
  );
}

/**
 * `GET /api/creators/{id}/flows?conversation_id=…` — la liste du menu Flow ▾,
 * annotée pour CE fan. La prod ne connaît pas nos conversations : on lui
 * demande la liste nue et on pose nous-mêmes « déjà reçu » / « un flow est
 * en cours », comme elle le ferait.
 */
export async function lesFlowsPour(creatorId: string, conversationId: string): Promise<FlowSummary[]> {
  const flows = await lesFlowsDe(creatorId);
  const conv = conversation(conversationId);
  const enCours = runs.get(conversationId)?.status === "running";
  return flows.map((f) => {
    const deja = conv?.flowsRecus.includes(f.name) ?? false;
    return {
      ...f,
      deja_lance: deja,
      disponible: !deja && !enCours,
      indisponible_parce_que: deja ? "deja_lance" : enCours ? "flow_en_cours" : null,
    };
  });
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Dérouler un flow                                                           */
/* ────────────────────────────────────────────────────────────────────────── */

/** Un pas joué : une bulle du flow, ou une attente de la réponse du fan. */
type Pas =
  | { de: "flow"; texte: string; media: { id: string; kind: string } | null; apresMs: number }
  | { de: "fan" };

/**
 * Le chemin du flow, du départ à la fin : on suit les arêtes depuis le nœud
 * `start`, la PREMIÈRE sortie à chaque fois (le chemin heureux — le fan de la
 * démo répond toujours). Un nœud déjà vu arrête la marche : un flow qui boucle
 * ne fait pas tourner la démo à l'infini.
 */
export function cheminDuFlow(flow: FlowDetail): Pas[] {
  const parId = new Map(flow.nodes.map((n) => [n.id, n]));
  const sorties = new Map<string, string[]>();
  for (const e of flow.edges_data ?? []) {
    const source = String(e.source ?? "");
    const cible = String(e.target ?? "");
    if (!source || !cible) continue;
    sorties.set(source, [...(sorties.get(source) ?? []), cible]);
  }

  const depart =
    flow.nodes.find((n) => n.node_type === "start" || n.node_type === "trigger") ?? flow.nodes[0];
  const pas: Pas[] = [];
  const vus = new Set<string>();
  let courant: FlowNodeBackend | undefined = depart;
  let pauseAvant = false;

  while (courant && !vus.has(courant.id) && vus.size < 60) {
    vus.add(courant.id);
    const d = (courant.data ?? {}) as Record<string, unknown>;
    if (courant.node_type === "suspend") pauseAvant = true;
    if (courant.node_type === "message") {
      const items = Array.isArray(d.items) ? (d.items as Record<string, unknown>[]) : [];
      for (const it of items) {
        const apresMs = pauseAvant ? PAUSE_MS : ENTRE_DEUX_BULLES_MS;
        pauseAvant = false;
        if (it.type === "text" && typeof it.content === "string" && it.content.trim()) {
          pas.push({ de: "flow", texte: it.content, media: null, apresMs });
        } else if (it.type === "media" && typeof it.vault_item_id === "string") {
          pas.push({
            de: "flow",
            texte: typeof it.caption === "string" ? it.caption : "",
            media: { id: it.vault_item_id, kind: String(it.media_kind ?? "image") },
            apresMs: PAUSE_MS,
          });
        }
      }
      if (d.wait_for_response) pas.push({ de: "fan" });
    }
    if (courant.node_type === "end_flow") break;
    const suivant: string | undefined = sorties.get(courant.id)?.[0];
    courant = suivant ? parId.get(suivant) : undefined;
  }
  // La dernière attente n'appelle pas de réponse « au flow » : c'est la
  // réponse de fin (`apresLeFlow`), jouée à part.
  while (pas.at(-1)?.de === "fan") pas.pop();
  return pas;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Les runs                                                                   */
/* ────────────────────────────────────────────────────────────────────────── */

const runs = new Map<string, FlowRun>();
const arrets = new Map<string, Array<() => void>>();

/** Le run de cette conversation : en cours, ou le dernier. */
export function leRun(conversationId: string): FlowRun | null {
  return runs.get(conversationId) ?? null;
}

export function leRunActif(conversationId: string): FlowRun | null {
  const r = runs.get(conversationId);
  return r && r.status === "running" ? r : null;
}

/** `POST /api/creators/{id}/flows/{flowId}/start` — joué ici, jamais en prod. */
export async function lancerLeFlow(
  creatorId: string,
  flowId: string,
  conversationId: string,
): Promise<FlowRun | null> {
  const conv = conversation(conversationId);
  if (!conv) return null;
  const flow = await leFlow(creatorId, flowId);
  if (!flow) return null;

  const pas = cheminDuFlow(flow);
  const bulles = pas.filter((p) => p.de === "flow").length;
  const run: FlowRun = {
    id: idDemo("run"),
    flow_id: flowId,
    conversation_id: conversationId,
    creator_id: creatorId,
    flow_name: flow.name,
    status: "running",
    current_node_id: null,
    current_node_entered_at: new Date().toISOString(),
    total_nodes: bulles,
    completed_nodes: 0,
    progress_percent: 0,
    error: null,
    started_at: new Date().toISOString(),
    completed_at: null,
    wait_kind: null,
    prochain_message: pas.find((p) => p.de === "flow")?.texte || null,
  };
  runs.set(conversationId, run);
  conv.flowsRecus = [...conv.flowsRecus, flow.name];
  conv.fiche.active_flow_run = carteDuRun(run);
  marquerLeFlow(conversationId, true);

  const annulations: Array<() => void> = [];
  arrets.set(conversationId, annulations);
  const reponses = [...(conv.pendantLeFlow ?? [])];

  let t = 1_500;
  let faites = 0;
  for (const p of pas) {
    if (p.de === "fan") {
      const dit = reponses.shift();
      if (!dit) continue;
      annulations.push(
        plusTard(() => emettre({ type: "typing", conversation_id: conversationId, until: new Date(Date.now() + 2_500).toISOString() }), t + 1_000),
      );
      t += REPONSE_DU_FAN_MS;
      annulations.push(plusTard(() => dire(conv, [dit]), t));
      continue;
    }
    t += p.apresMs;
    const ordre = ++faites;
    annulations.push(
      plusTard(() => {
        poserUneBulleDuFlow(conv, run, p);
        run.completed_nodes = ordre;
        run.progress_percent = Math.round((ordre / Math.max(1, bulles)) * 100);
        run.current_node_entered_at = new Date().toISOString();
        conv.fiche.active_flow_run = carteDuRun(run);
      }, t),
    );
  }

  annulations.push(
    plusTard(() => {
      terminer(conv, run, "completed");
      // Il répond à « tu vas devoir me payer » — c'est là que le chatteur
      // reprend, et que Francis passe au prix.
      if (conv.apresLeFlow?.length) dire(conv, conv.apresLeFlow);
      if (conv.scriptApresLeFlow) {
        conv.script = conv.scriptApresLeFlow.map((x) => ({ ...x }));
        conv.scriptApresLeFlow = undefined;
      }
    }, t + 2_500),
  );
  return { ...run };
}

/** Ce que la carte de la liste montre d'un run : sa progression. */
function carteDuRun(run: FlowRun): NonNullable<ConversationDemo["fiche"]["active_flow_run"]> {
  return {
    flow_id: run.flow_id,
    flow_name: run.flow_name,
    progress_percent: run.progress_percent,
    completed_nodes: run.completed_nodes,
    total_nodes: run.total_nodes,
    current_node_entered_at: run.current_node_entered_at,
  };
}

function poserUneBulleDuFlow(
  conv: ConversationDemo,
  run: FlowRun,
  p: Extract<Pas, { de: "flow" }>,
): void {
  const traduit = conv.langue === "fr" || !p.texte ? null : traduireVers(p.texte, conv.langue);
  const msg = poserLeMessage(
    conv,
    message({
      direction: "out",
      created_at: new Date().toISOString(),
      content: traduit ?? p.texte,
      message_type: p.media ? (p.media.kind === "video" ? "video" : p.media.kind === "audio" ? "voice" : "photo") : "text",
      sender_source: "flow",
      flow_id: run.flow_id,
      ...(p.media
        ? {
            media_file_id: p.media.id,
            media_url: `/api/vault/items/${encodeURIComponent(p.media.id)}/thumb`,
          }
        : {}),
      ...(traduit
        ? { traduction: { source: p.texte, depuis: "fr", vers: conv.langue, provenance: "llm" } }
        : {}),
    }),
  );
  emettre({ type: "new_message", conversation_id: conv.fiche.id, message: msg });
}

function terminer(conv: ConversationDemo, run: FlowRun, statut: "completed" | "stopped"): void {
  run.status = statut;
  run.completed_at = new Date().toISOString();
  if (statut === "completed") run.progress_percent = 100;
  conv.fiche.active_flow_run = null;
  if (statut === "completed") conv.fiche.moment_a_reprendre = "flow_termine";
  marquerLeFlow(conv.fiche.id, false);
  arrets.delete(conv.fiche.id);
  // `flow_run` : c'est ce qui fait relire la carte et les runs au cockpit.
  emettre({ type: "conversation_state_changed", conversation_id: conv.fiche.id, flow_run: statut });
}

/** `POST …/runs/{runId}/stop` — le chatteur coupe le flow. */
export function arreterLeFlow(runId: string): FlowRun | null {
  for (const [convId, run] of runs) {
    if (run.id !== runId) continue;
    for (const annuler of arrets.get(convId) ?? []) annuler();
    const conv = conversation(convId);
    if (conv && run.status === "running") terminer(conv, run, "stopped");
    return { ...run };
  }
  return null;
}

/** Un envoi du chatteur pendant un flow : comme au poste, son message passe,
 *  le flow continue — et le fan ne répond qu'au flow. Après le flow, il
 *  répond au chatteur. Exposé pour `serveur.ts`. */
export function reagirALenvoi(conv: ConversationDemo, geste: Parameters<typeof repliqueDuFan>[1]): void {
  if (leRunActif(conv.fiche.id)) return;
  repliqueDuFan(conv, geste);
}

/** Repart de zéro (nouveau monde). */
export function oublierLesFlows(): void {
  for (const annulations of arrets.values()) for (const a of annulations) a();
  arrets.clear();
  runs.clear();
  listes.clear();
  details.clear();
}

/** Les conversations où un flow tourne — pour le banc. */
export function conversationsEnFlow(): string[] {
  return toutesLesConversations()
    .filter((c) => leRunActif(c.fiche.id))
    .map((c) => c.fiche.id);
}
